
import React, { useState } from 'react';
import { QueueEntry, ReleaseRecord, PriorityCategory, UserRole } from '../types';
import { ICONS } from '../constants';

interface QueueDashboardProps {
  queue: QueueEntry[];
  releases: ReleaseRecord[];
  priorities: PriorityCategory[];
  userRole: UserRole;
  onJoinQueue: (name: string, dates: string[], priorityIds: string[], preferredZones: string[]) => void;
}

const QueueDashboard: React.FC<QueueDashboardProps> = ({ queue, releases, priorities, userRole, onJoinQueue }) => {
  const [name, setName] = useState('');
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);
  const [selectedZones, setSelectedZones] = useState<string[]>(['TABAL', '31GGS', '31GGS-EV']);

  const zones = ['TABAL', '31GGS', '31GGS-EV'];
  const today = new Date();
  today.setHours(0,0,0,0);
  const todayStr = today.toISOString().split('T')[0];

  const horizonWorkingDays = 10; 

  const dates = (() => {
    const validDates: string[] = [];
    const checkDate = new Date();
    if(checkDate.getDay() === 0) checkDate.setDate(checkDate.getDate() + 1);
    if(checkDate.getDay() === 6) checkDate.setDate(checkDate.getDate() + 2);

    while (validDates.length < horizonWorkingDays) {
      const day = checkDate.getDay();
      if (day !== 0 && day !== 6) {
        validDates.push(checkDate.toISOString().split('T')[0]);
      }
      checkDate.setDate(checkDate.getDate() + 1);
    }
    return validDates;
  })();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim() && selectedDates.length > 0 && selectedZones.length > 0) {
      onJoinQueue(name.trim(), selectedDates, selectedPriorities, selectedZones);
      setName('');
      setSelectedDates([]);
      setSelectedPriorities([]);
      setSelectedZones(['TABAL', '31GGS', '31GGS-EV']);
    }
  };

  const toggleDate = (date: string) => {
    setSelectedDates(prev => 
      prev.includes(date) ? prev.filter(d => d !== date) : [...prev, date]
    );
  };

  const togglePriority = (id: string) => {
    setSelectedPriorities(prev => 
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const toggleZone = (zone: string) => {
    setSelectedZones(prev => 
      prev.includes(zone) ? prev.filter(z => z !== zone) : [...prev, zone]
    );
  };

  const sortedQueue = [...queue].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-200 sticky top-8">
          <div className="mb-8">
            <h2 className="text-2xl font-black text-slate-900 tracking-tight">Request Access</h2>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em] mt-2">
              Corporate Office • 2 Week Booking Window
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">Personnel Member</label>
              <input 
                type="text" 
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter Full Name"
                className="w-full px-5 py-4 rounded-xl bg-slate-50 border-2 border-slate-50 focus:border-accent-red focus:bg-white transition-all outline-none font-bold text-slate-800"
                required
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Session Dates (10 Working Days Horizon)
                </label>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {dates.map(d => {
                  const dateObj = new Date(d);
                  const isSelected = selectedDates.includes(d);
                  const isPassed = d < todayStr;

                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => !isPassed && toggleDate(d)}
                      disabled={isPassed}
                      className={`flex flex-col items-center justify-center py-3 rounded-xl border-2 transition-all ${
                        isPassed
                          ? 'bg-slate-50 border-slate-50 opacity-30 grayscale cursor-not-allowed'
                          : isSelected 
                            ? 'bg-accent-red border-accent-red text-white shadow-lg shadow-red-100' 
                            : 'bg-white border-slate-100 text-slate-400 hover:border-slate-300'
                      }`}
                    >
                      <span className="text-[9px] font-black uppercase tracking-tight leading-none mb-1">
                        {dateObj.toLocaleDateString(undefined, { weekday: 'short' })}
                      </span>
                      <span className="text-sm font-black leading-none">
                        {dateObj.getDate()}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">Preferred Zones</label>
              <div className="flex flex-wrap gap-2">
                {zones.map(z => (
                  <button
                    key={z}
                    type="button"
                    onClick={() => toggleZone(z)}
                    className={`px-4 py-2 rounded-xl border-2 text-[10px] font-black uppercase tracking-widest transition-all ${
                      selectedZones.includes(z)
                        ? 'bg-brand-dark border-brand-dark text-white shadow-md'
                        : 'bg-white border-slate-100 text-slate-400 hover:border-slate-300'
                    }`}
                  >
                    {z}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 ml-1">Priority Categorization</label>
              <div className="space-y-2">
                {priorities.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => togglePriority(p.id)}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                      selectedPriorities.includes(p.id)
                        ? 'bg-red-50 border-accent-red text-accent-red'
                        : 'bg-white border-slate-100 text-slate-600 hover:border-slate-200'
                    }`}
                  >
                    <span className="text-xs font-black uppercase tracking-tight">{p.label}</span>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${selectedPriorities.includes(p.id) ? 'bg-accent-red border-accent-red' : 'border-slate-200'}`}>
                      {selectedPriorities.includes(p.id) && <ICONS.Check className="w-3 h-3 text-white" />}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <button 
              type="submit"
              disabled={selectedDates.length === 0 || selectedZones.length === 0}
              className={`w-full font-black py-4 rounded-xl transition-all flex items-center justify-center gap-3 shadow-xl ${
                selectedDates.length > 0 && selectedZones.length > 0
                  ? 'bg-accent-red text-white shadow-red-100 hover:bg-black' 
                  : 'bg-slate-100 text-slate-300 cursor-not-allowed border border-slate-200 shadow-none'
              }`}
            >
              <ICONS.Plus className="w-5 h-5" /> Submit Request
            </button>
          </form>
        </div>
      </div>

      <div className="lg:col-span-7 space-y-10">
        <div>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-black text-slate-900 tracking-tight flex items-center gap-3">
              <div className="w-2 h-8 bg-accent-red rounded-full" />
              Active Waiting List ({queue.length})
            </h3>
          </div>
          <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left min-w-[500px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Date</th>
                    <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Personnel</th>
                    <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Preferences</th>
                    <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-bold">
                  {sortedQueue.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-8 py-16 text-center text-slate-400 font-bold italic">
                        All requests processed.
                      </td>
                    </tr>
                  ) : (
                    sortedQueue.map((entry) => (
                      <tr key={entry.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-8 py-5 text-slate-900 text-xs font-black">
                          {new Date(entry.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', weekday: 'short' })}
                        </td>
                        <td className="px-8 py-5">
                          <div className="flex items-center gap-2">
                            <p className="font-black text-slate-900 text-sm leading-tight">{entry.userName}</p>
                            {entry.isVip && <span className="bg-amber-100 text-amber-600 text-[8px] font-black uppercase px-1.5 py-0.5 rounded flex items-center gap-1 border border-amber-200"><ICONS.Star className="w-2 h-2 fill-amber-600" /> VIP</span>}
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {entry.priorityIds.map(pid => {
                              const p = priorities.find(x => x.id === pid);
                              return p ? (
                                <span key={pid} className="text-[7px] font-black uppercase bg-red-50 text-accent-red px-1.5 py-0.5 rounded border border-red-100">
                                  {p.label}
                                </span>
                              ) : null;
                            })}
                          </div>
                        </td>
                        <td className="px-8 py-5">
                          <div className="flex flex-wrap gap-1">
                            {entry.preferredZones.map(z => (
                              <span key={z} className="px-1.5 py-0.5 rounded text-[8px] font-black border border-slate-200 uppercase bg-white text-slate-500">
                                {z}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-8 py-5">
                          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-[9px] font-black uppercase tracking-widest border border-amber-200">
                            <div className="w-1 h-1 rounded-full bg-amber-500 animate-pulse" />
                            Pending
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-3">
            <div className="w-2 h-8 bg-emerald-500 rounded-full" />
            Recent Allocations
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {releases.filter(r => r.isClaimed).slice(-6).reverse().map((claim) => (
              <div key={claim.id} className="bg-white border border-slate-200 p-5 rounded-2xl flex items-center gap-4 hover:border-emerald-500 transition-all group">
                <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shadow-sm group-hover:bg-emerald-600 group-hover:text-white transition-all">
                  <ICONS.Check className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-black text-slate-900 text-sm leading-none mb-1.5">{claim.claimedBy}</p>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">
                    Assigned: {new Date(claim.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QueueDashboard;
